import { ENV } from "@-/com.npmjs.varlock/env";

const TRAILING_SLASH_REGEX = /\/$/;
const LOOPBACK_HOST_REGEX = /^(?:127(?:\.\d+){3}|\[?::1\]?|localhost)$/i;

function normalizeBaseUrl(url: string): string {
  return url.replace(TRAILING_SLASH_REGEX, "");
}

/** Whether a Better Auth base URL points at the local dev server (unreachable from a real device). */
export function isLoopbackBetterAuthUrl(url: string): boolean {
  try {
    return LOOPBACK_HOST_REGEX.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

function isDeployedEnvironment(): boolean {
  return (
    ENV.EXPO_PUBLIC_APP_VARIANT === "production" ||
    ENV.EXPO_PUBLIC_APP_VARIANT === "staging" ||
    ENV.EXPO_PUBLIC_APP_VARIANT === "alpha"
  );
}

function readBetterAuthUrl(key: "BETTER_AUTH_URL"): string | undefined {
  const value = process.env[key]?.trim();
  if (value == null || value.length === 0) {
    return undefined;
  }
  return normalizeBaseUrl(value);
}

/**
 * Read the PUBLIC base URL, preferring the varlock `ENV` proxy.
 *
 * `EXPO_PUBLIC_*` vars are babel-inlined onto `ENV`, so that is the only source guaranteed to carry
 * a value on the deployed workerd runtime — `process.env` may simply not have it there, which is
 * precisely the environment this fallback exists to rescue. `process.env` is still consulted as a
 * secondary source so tests (and bare-Node callers) that set the variable directly keep working.
 */
function readPublicBetterAuthUrl(): string | undefined {
  const value = ENV.EXPO_PUBLIC_BETTER_AUTH_URL?.trim() ?? undefined;
  const resolved =
    value == null || value.length === 0 ? process.env.EXPO_PUBLIC_BETTER_AUTH_URL?.trim() : value;
  if (resolved == null || resolved.length === 0) {
    return undefined;
  }
  return normalizeBaseUrl(resolved);
}

/**
 * Staging release origin for native CI builds. CI sometimes loads Infisical `dev`
 * secrets where auth URLs are local loopback values; staging must provide an
 * explicit deployed origin instead of resolving a hardcoded default.
 */
export function resolveStagingBetterAuthBaseUrl(env: NodeJS.ProcessEnv = process.env): string {
  for (const key of [
    "STAGING_API_BASE_URL",
    "EXPO_PUBLIC_BETTER_AUTH_URL",
    "BETTER_AUTH_URL",
  ] as const) {
    const candidate = env[key]?.trim() ?? "";
    if (candidate.length === 0) {
      continue;
    }
    if (!isLoopbackBetterAuthUrl(candidate)) {
      return normalizeBaseUrl(candidate);
    }
  }
  throw new Error(
    "Staging Better Auth URL is required. Set STAGING_API_BASE_URL, " +
      "EXPO_PUBLIC_BETTER_AUTH_URL, or BETTER_AUTH_URL to a deployed non-loopback origin.",
  );
}

/**
 * Resolves the public Better Auth API origin for server-side config.
 *
 * `BETTER_AUTH_URL` is the server-side origin and still WINS whenever it names a usable one — the
 * two variables are allowed to differ, which is the point of having both (the server's own origin
 * need not be the public one baked into the client bundle).
 *
 * When it is missing or loopback in a DEPLOYED build, `EXPO_PUBLIC_BETTER_AUTH_URL` is used
 * instead. Production previously threw outright in that case, which is how a deployed build ended
 * up serving a hard 500 on every `/api/auth/*` request (`Refusing loopback Better Auth URL
 * http://127.0.0.1:4574`) while the correct public origin sat right there in the environment,
 * unread. Staging already resolved this way via {@link resolveStagingBetterAuthBaseUrl}; this
 * extends the same fallback to production rather than keeping two different behaviours.
 *
 * The guard is NOT weakened: the fallback is only accepted when it is itself non-loopback, so a
 * deployed build whose every candidate is a dev value still fails loudly instead of booting with an
 * origin no client can reach.
 */
export function resolveBetterAuthBaseUrl(): string {
  const fromEnv = readBetterAuthUrl("BETTER_AUTH_URL");
  if (
    // Alpha builds run against the staging backend.
    (ENV.EXPO_PUBLIC_APP_VARIANT === "staging" || ENV.EXPO_PUBLIC_APP_VARIANT === "alpha") &&
    (fromEnv == null || isLoopbackBetterAuthUrl(fromEnv))
  ) {
    return resolveStagingBetterAuthBaseUrl();
  }

  if (isDeployedEnvironment() && (fromEnv == null || isLoopbackBetterAuthUrl(fromEnv))) {
    const fromPublicEnv = readPublicBetterAuthUrl();
    if (fromPublicEnv != null && !isLoopbackBetterAuthUrl(fromPublicEnv)) {
      return fromPublicEnv;
    }
    if (fromEnv == null) {
      throw new Error(
        "BETTER_AUTH_URL is required. Set BETTER_AUTH_URL or EXPO_PUBLIC_BETTER_AUTH_URL to a " +
          `deployed non-loopback origin for the ${ENV.EXPO_PUBLIC_APP_VARIANT} build.`,
      );
    }
    throw new Error(
      `Refusing loopback Better Auth URL ${fromEnv} for ${ENV.EXPO_PUBLIC_APP_VARIANT} build.`,
    );
  }

  if (fromEnv == null) {
    throw new Error("BETTER_AUTH_URL is required.");
  }
  return fromEnv;
}

/**
 * Ensures `process.env.BETTER_AUTH_URL` is set before `betterAuth()` initializes.
 * Better Auth reads the env var internally even when `baseURL` is passed in options.
 */
export function syncBetterAuthBaseUrlEnv(): string {
  const baseURL = resolveBetterAuthBaseUrl();
  process.env.BETTER_AUTH_URL = baseURL;
  return baseURL;
}
